import { Script } from "gatsby"
import { useEffect, useMemo, useState, FC } from "react"
import { useSelector } from "react-redux"
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import { renderToStaticMarkup } from "react-dom/server"
import { INLINES } from "@contentful/rich-text-types"

// utils
import { events } from "../../utils/heap/tracking_events"
import { getFlag } from "../../utils/query_param_flags"
import { renderRichText } from "../../utils/contentful/render_rich_text"
import { extractLangCode } from "../../utils/general_utils"

// types
import { RootState } from "../../store/store"
import { DuetMetaData } from "../../types/decagon.duet.type"
import { DuetLocalizationData } from "../../types/browser"

// styles
import * as styles from "./ChatBot.module.scss"
import { userProfileGuardCheck } from "../../store/slices/user_profile.slice"

const SCRIPT_SRC = process.env.GATSBY_CHATBOT_SCRIPTS
  ? typeof process.env.GATSBY_CHATBOT_SCRIPTS === "string"
    ? process.env.GATSBY_CHATBOT_SCRIPTS
    : ""
  : ""

if (SCRIPT_SRC?.length <= 0) {
  throw new Error("GATSBY_CHATBOT_SCRIPTS length <= 0")
}

export const ChatBot: FC<{
  chatbotUITranslations: Queries.HomepageDataQuery["chatbotUITranslations"]
  onChatbotClick: () => void
}> = ({ chatbotUITranslations, onChatbotClick }) => {
  const [duetScriptLoaded, setDuetScriptLoaded] = useState(false)
  const [hasFiredDuetMetadata, setHasFiredDuetMetadata] = useState(false)
  const [duetIframe, setDuetIframe] = useState<HTMLIFrameElement>()
  const [isDuetReady, setIsDuetReady] = useState(false)

  const defaultIframeDimensions = { height: "700px", width: "500px" }

  const urlParamFlag = getFlag()

  const chatbotUITranslationsMicropy = useMemo<DuetLocalizationData>(() => {
    const finalTranslationData: DuetLocalizationData = {
      widgetText: "Ask a question",
      chatTitle: "Chat with Eventbrite AI",
      waitTime: "Less than a minute",
      userTypePrompt: "I am...",
      userTypes: {
        attendee: "Attending an event",
        organizer: "Organizing an event"
      },
      conversationHistoryTitle: "My conversations",
      newConversation: "Send us a new message",
      noConversations: "No conversations yet",
      disclaimerHTML:
        '<p>Chat sessions are powered and recorded by Eventbrite and our vendors. By chatting, you agree to our <a href="https://www.eventbrite.com/help/en-us/articles/251210/eventbrite-terms-of-service/" target="_blank" class="text-blue-200 font-bold">Terms</a> and <a href="https://www.eventbrite.com/help/en-us/articles/460838/eventbrite-privacy-policy/" target="_blank" class="text-blue-200 font-bold">Privacy Policy</a>.</p>',
      inputPlaceholder: "Ask a detailed question...",
      readMore: "read more"
    }

    if (chatbotUITranslations.edges?.length > 0) {
      chatbotUITranslations.edges[0].node?.microcopySetEntries?.forEach((y) => {
        if (
          y &&
          "microcopyKey" in y &&
          y.microcopyValue?.raw &&
          typeof y.microcopyKey === "string"
        ) {
          const parsedMicrocopyValue = JSON.parse(y.microcopyValue.raw)
          const value = documentToPlainTextString(parsedMicrocopyValue)

          if (y.microcopyKey?.trim() === "selectionAttendingAnEvent") {
            finalTranslationData.userTypes["attendee"] = value
          } else if (y.microcopyKey?.trim() === "selectionOrganizingAnEvent") {
            finalTranslationData.userTypes["organizer"] =
              documentToPlainTextString(JSON.parse(y.microcopyValue.raw))
          } else if (y.microcopyKey?.trim() === "disclaimerHTML") {
            const renderedRichText = renderRichText(y.microcopyValue.raw, {
              renderNode: {
                [INLINES.HYPERLINK]: (node, children) => {
                  const uri = node.data?.uri as string

                  return (
                    <a
                      href={uri}
                      target="_blank"
                      className="text-blue-200 font-bold"
                    >
                      {children}
                    </a>
                  )
                }
              }
            })

            if (renderedRichText) {
              finalTranslationData[y.microcopyKey] =
                renderToStaticMarkup(renderedRichText)
            }
          } else {
            finalTranslationData[y.microcopyKey] = documentToPlainTextString(
              JSON.parse(y.microcopyValue.raw)
            )
          }
        }
      })
    }

    return finalTranslationData
  }, [chatbotUITranslations])

  if (typeof window !== "undefined") {
    const langCode =
      useSelector((state: RootState) => state.metaData.langCode) ||
      extractLangCode()
    const userProfile = userProfileGuardCheck(
      useSelector((state: RootState) => state.userProfile)
    )

    const chatbotApiCallsCompleted = useSelector(
      (state: RootState) => state.metaData.chatbotApiCallsCompleted
    )
    const chatbotTreatmentEnabled = useSelector(
      (state: RootState) => state.metaData.chatbotTreatmentEnabled
    )

    // The AI chatbot iframe is always injected, but its visibility is controlled
    // via the `shouldShowAIChatbot` Redux flag. This replaces older logic that
    // inferred visibility from a combination of live chat state flags.
    const shouldRenderBot = useSelector((state: RootState) => {
      return state.metaData.shouldShowAIChatbot
    })

    useEffect(() => {
      if (isDuetReady) {
        window?.duet?.setLocalizationData(chatbotUITranslationsMicropy)
      }
    }, [isDuetReady, duetIframe, chatbotUITranslationsMicropy])

    useEffect(() => {
      // Function to check for the object
      const checkForDuet = () => {
        if (window.duet?.isReady() && !isDuetReady) {
          setIsDuetReady(true)
        }
      }

      checkForDuet()

      const intervalId = setInterval(checkForDuet, 100)

      if (isDuetReady) {
        clearInterval(intervalId)
      }

      // Clean up the interval on component unmount
      return () => clearInterval(intervalId)
    }, [isDuetReady])

    useEffect(() => {
      if (!duetIframe) {
        Object.values(document.getElementsByTagName("iframe")).forEach((x) => {
          if (x.src.includes("decagon")) {
            x.style.display = "none"

            setDuetIframe(x)
          }
        })
      }

      if (duetIframe) {
        if (chatbotTreatmentEnabled) {
          // Enhanced debug logging
          process.env.GATSBY_DEBUG === "true" &&
            console.log("ChatBot widget visibility debug:", {
              shouldRenderBot,
              chatbotApiCallsCompleted
            })
          if (shouldRenderBot && chatbotApiCallsCompleted) {
            duetIframe.style.display = "block"
          } else {
            duetIframe.style.display = "none"
          }
        } else {
          if (shouldRenderBot) {
            duetIframe.style.display = "block"
          }
        }
        if (!shouldRenderBot) {
          duetIframe.style.display = "none"
        }
      }
    })

    return (
      <>
        {shouldRenderBot && (
          <a className={styles?.chatbotContainer}>
            {SCRIPT_SRC && langCode && (
              <Script
                id="chatBot"
                type="module"
                src={SCRIPT_SRC}
                onLoad={(ev) => {
                  window?.duet?.setEventListener((event) => {
                    if (event?.eventName?.length > 0) {
                      events?.duetEvent(event?.eventName)

                      if (event.eventName === "DUET_INITIALIZE") {
                        process.env.GATSBY_DEBUG === "true" &&
                          console.log(
                            "Attempting to send duet langCode metadata"
                          )

                        if (!langCode || langCode === "UNKNOWN") {
                          console.warn(
                            "No langCode is available to send for chatbot",
                            { langCode }
                          )
                        }

                        // set metadata if eventname is DUET_INITIALIZE
                        if (
                          window?.duet?.setMetadata &&
                          langCode &&
                          !hasFiredDuetMetadata
                        ) {
                          const metaDataObj = {} as DuetMetaData

                          if (!langCode || langCode === "UNKNOWN") {
                            metaDataObj.langCode = extractLangCode() || ""
                          }

                          if (window?.location) {
                            metaDataObj.path = window.location.pathname
                            metaDataObj.url = window.location.href
                          }

                          if (window?.location) {
                            if (
                              window.location?.pathname &&
                              window?.duet?.setMetadata
                            ) {
                              process.env.GATSBY_DEBUG === "true" &&
                                console.log({
                                  updated_url: window.location.pathname
                                })

                              metaDataObj.updated_url = window.location.pathname
                            }
                          }

                          metaDataObj.langCode = langCode
                          process.env.GATSBY_DEBUG === "true" &&
                            console.log("Duet metadata object: ", metaDataObj)

                          window?.duet.addMetadata(metaDataObj)

                          process.env.GATSBY_DEBUG === "true" &&
                            console.log("Finished sending metadata to duet")

                          setHasFiredDuetMetadata(true)
                        }
                      }
                      if (event.eventName === "DECAGON_LOADED") {
                        onChatbotClick()
                      }
                    }
                  })

                  setDuetScriptLoaded(true)
                }}
              />
            )}
          </a>
        )}
      </>
    )
  }

  return null
}
